import { useContext, useEffect, useMemo, useState } from 'react';
import './ShopBranding.css';
import ConstructorBlock from '../../../Constructor/ConstructorBlock/ConstructorBlock';
import ImagesInput from '../../../ImagesInput/ImagesInput';
import ErrorMessage from '../../../../ErrorMessage/ErrorMessage';
import mainApi from '../../../../../assets/api/MainApi';
import { DEFAULT_MAX_IMG_SIZE } from '../../../../../assets/utils/constants';
import { UserContext } from '../../../../../assets/contexts/userContext';
import useAutoDismissError from '../../../../../assets/hooks/useAutoDismissError';
import { parseApiError } from '../../../../../assets/utils/utils';
import { CrossIcon } from '../../../../../assets/icons/controls/controls';
import { fetchStaticPagesData, saveStaticPageData } from '../../PagesAndFiles/components/EditMainPage/services/editMainPageApi';

const initialBrandingValues = {
  logo: [],
  icon: [],
};

function ShopBranding({ onClose, isOpen }) {
  const { user, setUser } = useContext(UserContext);
  const shopId = user?.default_shop?._id;
  const [values, setValues] = useState(initialBrandingValues);
  const [constructorBlocks, setConstructorBlocks] = useState([]);
  const [layout, setLayout] = useState([]);
  const [initialValues, setInitialValues] = useState(initialBrandingValues);
  const [initialLayout, setInitialLayout] = useState([]);
  const [initialIds, setInitialIds] = useState({
    logo: null,
    icon: null,
  });
  const [preloaders, setPreloaders] = useState({
    initial: true,
    save: false,
  });
  const [error, showError] = useAutoDismissError();

  useEffect(() => {
    if (!shopId || !isOpen) return;
    setPreloaders((prevValue) => ({
      ...prevValue,
      initial: true,
    }));
    Promise.allSettled([
      mainApi.getConstructorMainPage({ shop_id: shopId }),
      fetchStaticPagesData(shopId),
    ])
      .then(([metaResult, layoutResult]) => {
        if (metaResult.status === 'fulfilled') {
          const res = metaResult.value;
          const logo = res?.logo || null;
          const icon = res?.icon || null;
          setValues({
            logo: logo ? [logo] : [],
            icon: icon ? [icon] : [],
          });
          setInitialValues({
            logo: logo ? [logo] : [],
            icon: icon ? [icon] : [],
          });
          setConstructorBlocks(res?.blocks || []);
          setInitialIds({
            logo: logo?._id || null,
            icon: icon?._id || null,
          });
        } else {
          showError(parseApiError(metaResult.reason));
        }

        if (layoutResult.status === 'fulfilled') {
          setLayout(layoutResult.value || []);
          setInitialLayout(layoutResult.value || []);
        } else {
          showError(parseApiError(layoutResult.reason));
          setLayout([]);
          setInitialLayout([]);
        }
      })
      .finally(() => {
        setPreloaders((prevValue) => ({
          ...prevValue,
          initial: false,
        }));
      });
  }, [shopId, isOpen, showError]);

  const currentIds = useMemo(() => ({
    logo: values.logo?.[0]?._id || null,
    icon: values.icon?.[0]?._id || null,
  }), [values]);

  const isDirty = useMemo(() => {
    return currentIds.logo !== initialIds.logo || currentIds.icon !== initialIds.icon;
  }, [currentIds, initialIds]);

  function handleFilesChange(file, name) {
    if (!file) return;
    if (file.size / 1000000 >= DEFAULT_MAX_IMG_SIZE) return;

    const data = new FormData();
    data.append('image', file);
    mainApi.uploadImage({ data, type: `${name}s` })
      .then((res) => {
        setValues((prevValue) => ({
          ...prevValue,
          [name]: [res],
        }));
      })
      .catch((err) => {
        showError(parseApiError(err));
      });
  }

  function handleChange(e) {
    const input = e.target;
    const name = input.name;

    if (name === 'logo' || name === 'icon') {
      handleFilesChange(input.files?.[0], name);
      return;
    }

    if (name === 'logo-delete') {
      setValues((prevValue) => ({ ...prevValue, logo: [] }));
      return;
    }

    if (name === 'icon-delete') {
      setValues((prevValue) => ({ ...prevValue, icon: [] }));
    }
  }

  function handleCancel() {
    setValues(initialValues);
    setLayout(initialLayout);
  }

  function getBlockId(item) {
    return item?.block?._id || item?.block?.id || item?.block_id || item?.blockId || null;
  }

  function buildLayoutForSave(blockIds, currentLayout) {
    const layoutMap = new Map((currentLayout || []).map((item) => [getBlockId(item), item]));
    return blockIds.map((blockId) => {
      if (layoutMap.has(blockId)) {
        const layoutItem = layoutMap.get(blockId);
        return {
          ...layoutItem,
          block: {
            ...(layoutItem.block || {}),
            _id: blockId,
          },
          data: Array.isArray(layoutItem.data) ? layoutItem.data : null,
        };
      }
      return {
        block: { _id: blockId },
        data: null,
      };
    });
  }

  async function handleSave() {
    if (!shopId || preloaders.save) return;
    setPreloaders((prevValue) => ({
      ...prevValue,
      save: true,
    }));
    try {
      const blockIds = constructorBlocks.map((item) => item?._id || item?.id).filter(Boolean);
      const layoutForSave = buildLayoutForSave(blockIds, layout);

      await mainApi.setConstructorMainPage({
        shop_id: shopId,
        logo: currentIds.logo,
        icon: currentIds.icon,
        blocks: blockIds,
      });

      await saveStaticPageData(shopId, layoutForSave);

      setInitialValues(values);
      setInitialIds(currentIds);
      setInitialLayout(layoutForSave);
      setLayout(layoutForSave);
      setUser((prevValue) => ({
        ...prevValue,
        default_shop: {
          ...prevValue.default_shop,
          logo: values.logo?.[0] || null,
          icon: values.icon?.[0] || null,
        },
      }));
    } catch (err) {
      showError(parseApiError(err));
    } finally {
      setPreloaders((prevValue) => ({
        ...prevValue,
        save: false,
      }));
    }
  }

  return (
    <div className='shop-branding-popup'>
      <div className='shop-branding-popup__header'>
        <p className='shop-branding-popup__title'>Логотипы магазина</p>
        <button
          className='shop-branding-popup__close'
          type='button'
          onClick={onClose}
        >
          <CrossIcon
            mainClassName='shop-branding-popup__close-icon'
            strokeClassName='shop-branding-popup__close-icon-stroke'
          />
        </button>
      </div>
      <ErrorMessage error={error} />
      <div className='shop-branding-popup__body'>
        <ConstructorBlock>
          <ImagesInput
            labelClassName='shop-branding-popup__img-input-label'
            name='logo'
            label='Логотип'
            hint={`154 × 40 px, не более ${DEFAULT_MAX_IMG_SIZE} Мб`}
            addBtnText='Изменить...'
            images={values.logo}
            isHorizontal
            onAdd={handleChange}
            onRemove={() => {
              handleChange({ target: { name: 'logo-delete' } });
            }}
            isPreloaderVisible={preloaders.initial}
          />
          <ImagesInput
            labelClassName='shop-branding-popup__img-input-label'
            name='icon'
            label='Иконка'
            hint={`785 × 785 px, не более ${DEFAULT_MAX_IMG_SIZE} Мб`}
            addBtnText='Изменить...'
            images={values.icon}
            isHorizontal
            onAdd={handleChange}
            onRemove={() => {
              handleChange({ target: { name: 'icon-delete' } });
            }}
            isPreloaderVisible={preloaders.initial}
          />
        </ConstructorBlock>
      </div>
      <div className='shop-branding-popup__actions'>
        <button
          className='shop-branding-popup__action-btn'
          type='button'
          onClick={handleCancel}
          disabled={!isDirty || preloaders.save}
        >
          Отменить
        </button>
        <button
          className='shop-branding-popup__action-btn shop-branding-popup__action-btn_main'
          type='button'
          onClick={handleSave}
          disabled={!isDirty || preloaders.save}
        >
          {preloaders.save ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}

export default ShopBranding;
